import type { InspirationMediaType, PortalInspirationCard } from '../../types/portalInspiration.js';

// PORTAL INSPIRATION — the CLIENT page's view logic. PURE. The server has already decided what this
// viewer may see; this only filters what it was given.

export type UsageChip = 'all' | 'ads' | 'organic';

export const USAGE_CHIPS: ReadonlyArray<{ id: UsageChip; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'ads', label: 'Ads' },
  { id: 'organic', label: 'Organic' },
];

export type ClientFilters = { mediaType: InspirationMediaType; usage: UsageChip; tag: string };

export const DEFAULT_CLIENT_FILTERS: ClientFilters = { mediaType: 'video', usage: 'all', tag: '' };

/** ⚠️ "Both" belongs under BOTH chips: an idea that works for ads and for organic posts is shown whichever is chosen. */
export function matchesUsage(card: Pick<PortalInspirationCard, 'usage'>, chip: UsageChip): boolean {
  if (chip === 'all') return true;
  if (card.usage === 'both') return true;
  return chip === 'ads' ? card.usage === 'advertising' : card.usage === 'organic';
}

export function filterCards(cards: readonly PortalInspirationCard[], f: ClientFilters): PortalInspirationCard[] {
  return cards.filter(
    (c) => c.mediaType === f.mediaType && matchesUsage(c, f.usage) && (f.tag === '' || c.tags.includes(f.tag)),
  );
}

/** Tag chips for the CURRENT tab (a tag that only appears on the other tab would filter to nothing). */
export function tagChips(cards: readonly PortalInspirationCard[], mediaType: InspirationMediaType): string[] {
  return [...new Set(cards.filter((c) => c.mediaType === mediaType).flatMap((c) => c.tags))].sort();
}

export function tabCounts(cards: readonly PortalInspirationCard[]): Record<InspirationMediaType, number> {
  return {
    video: cards.filter((c) => c.mediaType === 'video').length,
    image: cards.filter((c) => c.mediaType === 'image').length,
  };
}

/** A tag that vanished on refetch resolves to "all tags" rather than an empty page. */
export function effectiveTag(tag: string, available: readonly string[]): string {
  return tag !== '' && available.includes(tag) ? tag : '';
}

export const INSPIRATION_FOOTER = 'Shoot vertical · 9:16 · natural light · no filter · phone is fine';

export const INSPIRATION_INTRO =
  'Photo and video ideas from the Kyck team, with a short how-to for each. Pick one, shoot it on your phone, and use it to improve your content.';

export function emptyCopy(mediaType: InspirationMediaType, filtered: boolean): string {
  if (filtered) return 'Nothing matches those filters. Try another.';
  return mediaType === 'video' ? 'No video ideas here yet — check back soon.' : 'No photo ideas here yet — check back soon.';
}
